/**
 * ClassCard Component
 * Displays a single class card with actions
 */

import React from 'react';
import type { Class } from '@shared/types/teach';
import { Users, Edit, Trash2, Calendar } from 'lucide-react';

interface ClassCardProps {
  class: Class;
  onEdit: () => void;
  onManageMembers: () => void;
  onDelete: () => void;
}

export const ClassCard: React.FC<ClassCardProps> = ({
  class: cls,
  onEdit,
  onManageMembers,
  onDelete,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow">
      {/* Class name */}
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-1">
        {cls.name}
      </h3>

      {/* Description */}
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2 min-h-[2.5rem]">
        {cls.description || '暂无描述'}
      </p>

      {/* Stats */}
      <div className="flex items-center justify-between mb-4 text-sm text-gray-500 dark:text-gray-400">
        <div className="flex items-center space-x-1">
          <Users className="w-4 h-4" />
          <span>{cls.memberCount} 名学生</span>
        </div>
        <div className="flex items-center space-x-1">
          <Calendar className="w-4 h-4" />
          <span>{formatDate(cls.createdAt)}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center space-x-2">
        <button
          onClick={onEdit}
          className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-sm font-medium"
        >
          <Edit className="w-4 h-4 mr-1" />
          编辑
        </button>
        <button
          onClick={onManageMembers}
          className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors text-sm font-medium"
        >
          <Users className="w-4 h-4 mr-1" />
          成员
        </button>
        <button
          onClick={onDelete}
          className="inline-flex items-center justify-center px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
